//* Importing libraries from react
import React, { useState, useCallback, useEffect } from "react";

//* Importing libraries from react-router-dom
import { useNavigate } from "react-router-dom";

//* Importing components from react-bootstrap
import { List } from "../../../component/list/list";

//* Importing component from react
import { Loading } from "../../../component/common/Loading";

//* Importing hooks from react
import { useTags } from "../../../hooks/tags/useTags";

//* Importing services from apis
import { AuthAPI } from "../../../services/apis/AuthAPI";

//* Importing enum from constants
import { UserRole } from "../../../constants/enum/UserRole";

type Tag = {
  id: string;
  name: string;
  color: string; // Certifique-se de que 'color' está incluído
};

type User = {
  role: UserRole;
};

var debounce = require("lodash.debounce");

export function TagsView() {
  const token = localStorage.getItem("token") || "";
  const navigate = useNavigate();

  const [me, setMe] = useState<User | null>(null);
  const [showWarning, setShowWarning] = useState(false);
  const [tagToDelete, setTagToDelete] = useState<string | null>(null);
  const [searchInputValue, setSearchInputValue] = useState<string>("");
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [totalTags, setTotalTags] = useState<number>(0);

  const {
    tags,
    loading,
    error,
    setSearch,
    pagination,
    setPagination,
    deleteTag,
    refetch,
    changePage,
    isDeleting,
  } = useTags(token);

  useEffect(() => {
    async function fetchMe() {
      try {
        const response = await AuthAPI.me(token);
        setMe(response.data);
      } catch (error) {
        console.error("Error fetching user details:", error);
      }
    }
    fetchMe();
  }, [token]);

  useEffect(() => {
    setTotalTags(pagination.totalItems || 0);
  }, [pagination.totalItems]);

  const debouncedSearch = useCallback(
    debounce((value: string) => {
      if (value.trim().length >= 3) {
        setSearch(value);
      } else {
        setSearch("");
        setPagination((prev) => ({ ...prev, currentPage: 1 }));
      }
      setIsSearching(false);
    }, 500),
    [setSearch, setPagination]
  );

  const handleSearchInputChange = useCallback(
    (value: string) => {
      setSearchInputValue(value);
      setIsSearching(true);
      debouncedSearch(value);
    },
    [debouncedSearch]
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (tagToDelete) {
      try {
        const currentPageItems = tags.length;
        const currentPage = pagination.currentPage;

        await deleteTag(tagToDelete);

        if (currentPageItems === 1 && currentPage > 1) {
          changePage(currentPage - 1);
        }

        setShowWarning(false);
        setTagToDelete(null);
      } catch (error) {
        console.error("Error deleting tag:", error);
      }
    }
  }, [tagToDelete, deleteTag, tags.length, pagination.currentPage, changePage]);

  const handleDeleteActionClick = useCallback((id: string) => {
    setTagToDelete(id);
    setShowWarning(true);
  }, []);

  const handleChangePagination = useCallback(
    (value: number) => {
      changePage(value);
    },
    [changePage]
  );

  if (!me || isDeleting) {
    return <Loading />;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  // Função para renderizar o campo de cor utilizando Bootstrap
  const renderColor = (item: Tag) => (
    <div className="d-flex justify-content-center align-items-center">
      <div
        className="me-2"
        style={{
          width: "24px",
          height: "24px",
          backgroundColor: item.color,
          border: "1px solid #000",
          borderRadius: "4px",
        }}
      ></div>
      <span>{item.color}</span>
    </div>
  );

  return (
    <div className="row">
      <List
        search_input_value={searchInputValue}
        hideManager
        search_text="Pesquise pelo nome da tag"
        onChangeSearchInput={handleSearchInputChange}
        onSearchButtonClick={refetch}
        title={`Tags (Total: ${totalTags})`}
        not_found_message="Nenhuma tag encontrada"
        button_link="/tags/create"
        button_text="Adicionar tag"
        deleting_message="Ao deletar esta tag, ela não estará mais disponível no sistema"
        onDeleteCancel={() => setShowWarning(false)}
        onDeleteConfirm={handleDeleteConfirm}
        onDeleteActionClick={handleDeleteActionClick}
        onEditActionClick={(id) => navigate(`/tags/edit/${id}`)}
        onChangePagination={handleChangePagination}
        show_warning={showWarning}
        loading_screen={loading || isSearching}
        items={tags.map((tag: Tag) => ({
          ...tag,
          key: tag.id,
        }))}
        pagination={{
          last_page: pagination.totalPages,
          current_page: pagination.currentPage,
        }}
        fields={[
          { name: "name", type: "text", header: "Nome" },
          { name: "color", type: "custom", header: "Cor", render: renderColor }, // Campo custom
        ]}
        loading_search={isSearching}
      />
    </div>
  );
}
